/**
 * Date Utilities for Timezone-Safe Operations
 * Handles timezone differences between local and serverless environments
 */

/**
 * Parse a date string and ensure it's treated as UTC
 * This fixes timezone issues between local (IST) and serverless (UTC) environments
 */
export function parseUTCDate(dateString: string | null | undefined): Date | null {
  if (!dateString) return null;

  const raw = String(dateString).trim()
  if (!raw) return null

  // Support epoch timestamps (seconds or ms)
  if (/^\d+$/.test(raw)) {
    const num = Number(raw)
    if (!Number.isFinite(num)) return null
    const ms = raw.length === 10 ? num * 1000 : num
    const date = new Date(ms)
    if (isNaN(date.getTime())) {
      console.warn('[dateUtils] Invalid epoch date string:', dateString)
      return null
    }
    return date
  }

  const hasTimeZoneInfo =
    /[zZ]$/.test(raw) ||
    /[+-]\d{2}:?\d{2}$/.test(raw) ||
    /[+-]\d{2}$/.test(raw)

  let normalized = raw

  // If the string has no timezone info, treat it as UTC.
  // This is important because MySQL TIMESTAMP/DATETIME values are often returned without timezone.
  if (!hasTimeZoneInfo) {
    // Date only: YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      normalized = `${raw}T00:00:00Z`
    }
    // MySQL-style: YYYY-MM-DD HH:mm[:ss[.SSS]]
    else if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2}(\.\d{1,6})?)?$/.test(raw)) {
      normalized = `${raw.replace(' ', 'T')}Z`
    }
    // ISO without timezone: YYYY-MM-DDTHH:mm[:ss[.SSS]]
    else if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,6})?)?$/.test(raw)) {
      normalized = `${raw}Z`
    }
  }

  const date = new Date(normalized)

  if (isNaN(date.getTime())) {
    console.warn('[dateUtils] Invalid date string:', dateString)
    return null
  }

  return date
}

/**
 * Format time for conversation list
 * Shows: "6:32 AM" for today, "Yesterday" for yesterday, "MM/DD/YYYY" for older
 * 
 * IMPORTANT: This function is timezone-aware and works correctly in both
 * local (IST) and serverless (UTC) environments
 */
export function formatConversationTime(dateString: string | null | undefined): string {
  if (!dateString) return '';

  const date = parseUTCDate(dateString);
  if (!date) return '';

  const now = new Date();

  // Get today's date at midnight in local timezone for comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  // Calculate difference in days
  const diffInDays = Math.floor((today.getTime() - messageDate.getTime()) / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) {
    // Today - show time in 12-hour format with AM/PM
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12; // Convert to 12-hour format (0 becomes 12)
    const displayMinutes = minutes.toString().padStart(2, '0');

    return `${displayHours}:${displayMinutes} ${ampm}`;
  } else if (diffInDays === 1) {
    // Yesterday
    return 'Yesterday';
  } else {
    // Older than yesterday - show date in DD/MM/YYYY format (no day names like Mon, Tue)
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  }
}

/**
 * Format date separator for message groups (WhatsApp-style)
 * Shows: "Today", "Yesterday", "Wednesday" (within last 7 days),
 * or "Wednesday, March 15, 2026" for older dates
 */
export function formatDateSeparator(dateString: string | null | undefined): string {
  if (!dateString) return '';

  const date = parseUTCDate(dateString);
  if (!date) return '';

  const now = new Date();

  // Get today's date at midnight in local timezone for comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  // Calculate difference in days
  const diffInDays = Math.floor((today.getTime() - messageDate.getTime()) / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) {
    return 'Today';
  } else if (diffInDays === 1) {
    return 'Yesterday';
  } else {
    // Show "weekday, month day, year" like WhatsApp (e.g. "Wednesday, March 15, 2026")
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  }
}

/**
 * Check if two dates are on the same day
 */
export function isSameDay(date1: string | null | undefined, date2: string | null | undefined): boolean {
  if (!date1 || !date2) return false;

  const d1 = parseUTCDate(date1);
  const d2 = parseUTCDate(date2);

  if (!d1 || !d2) return false;

  return (
    d1.getDate() === d2.getDate() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getFullYear() === d2.getFullYear()
  );
}

/**
 * Format full date and time for message timestamps
 * Shows: "Jan 15, 2024 at 6:32 PM"
 */
export function formatMessageTime(dateString: string | null | undefined): string {
  if (!dateString) return '';

  const date = parseUTCDate(dateString);
  if (!date) return '';

  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

/**
 * Format relative time (e.g., "2 minutes ago", "1 hour ago")
 */
export function formatRelativeTime(dateString: string | null | undefined): string {
  if (!dateString) return '';

  const date = parseUTCDate(dateString);
  if (!date) return '';

  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'Just now';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  } else if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  } else {
    return formatConversationTime(dateString);
  }
}

/**
 * Check if a date is today
 */
export function isToday(dateString: string | null | undefined): boolean {
  if (!dateString) return false;

  const date = parseUTCDate(dateString);
  if (!date) return false;

  const now = new Date();
  return (
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear()
  );
}

/**
 * Check if a date is yesterday
 */
export function isYesterday(dateString: string | null | undefined): boolean {
  if (!dateString) return false;

  const date = parseUTCDate(dateString);
  if (!date) return false;

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  return (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  );
}

/**
 * Convert local date to UTC ISO string for API calls
 */
export function toUTCString(date: Date): string {
  return date.toISOString();
}

/**
 * Get current UTC timestamp as ISO string
 */
export function nowUTC(): string {
  return new Date().toISOString();
}
